import { Component, computed, inject, OnInit } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { Broadcaster } from 'src/app/services/events/broadcaster.class';
import { CHANNELS } from 'src/app/services/events/event.service';
import { IPrintJobRelease } from 'src/app/services/release-history/models/IPrintJobRelease.model';
import { IPrintJobReleaseResource } from 'src/app/services/release-history/models/IPrintJobReleaseResource.model';
import { ReleaseHistoryService } from 'src/app/services/release-history/release-history.service';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { UserService } from 'src/app/services/user/user.service';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Router } from '@angular/router';
import { NetworkService } from 'src/app/services/network/network.service';
import { LocaleService } from 'src/app/services/language/locale.service';
import { ISignInMethods } from '@app/services/user/models/user.model';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { ErrorService } from '@app/services/error/error.service';

@Component({
  selector: 'app-release-history',
  templateUrl: './release-history.page.html',
  styleUrls: ['./release-history.page.scss'],
})
export class ReleaseHistoryPage implements OnInit {

  private router = inject(Router);

  private acceptedReleaseStates: Array<string> = ['RELEASED', 'TIMEOUT', 'FINISHED'];
  public appIsActive = true;
  private createdReleaseSubscription: Subscription = null;
  public currentLanguage: string = null;
  public currentPage: number = 0;
  public dataIsLoaded: boolean = false;
  private deletedReleaseSubscription: Subscription = null;
  public platformResumeEventSubscription: Subscription = null;
  public languageChangeSubscription: Subscription = null;
  private logger: Logger;
  public myInput;
  public nav: any;
  public pagingObject: any = {};
  private pageSize: number = 5;
  public printJobReleases: Array<IPrintJobRelease> = null;
  private refresher: any = null;
  public tenant = this.tenantService.tenantSignal;
  public getTenantAndUserSubscription = new Subject<void>();
  private unSubscribe = new Subject<void>();
  private updatedReleaseSubscription: Subscription = null;
  public  user = this.userService.userSignal;
  public isOnline = this.networkService.hasInternetAccess;

  public signInMethods: ISignInMethods = {
    LOCAL_AD: 'AD',
    AZURE: 'Microsoft',
    EMAIL: 'Email',
    GOOGLE: 'Google',
    OKTA: 'Okta',
    ONELOGIN: 'OneLogin',
    VERTICAL: 'Vertical',
    client_credentials: 'API',
    KIOSK: 'Kiosk',
    OIDC: 'OIDC'
  };

  constructor(
    private broadcaster: Broadcaster,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private localeService: LocaleService,
    private loggingService: LoggingService,
    private modalCtrl: ModalController,
    private networkService: NetworkService,
    private pageTitleService: PageTitleService,
    private platform: Platform,
    private releaseHistoryService: ReleaseHistoryService,
    private tenantService: TenantService,
    public translateService: TranslateService,
    private userService: UserService
  ) {
    this.logger = this.loggingService.getLogger("[ReleaseHistoryPage]");
    const methodName = "ctor";
    this.logger.entry(methodName);

    this.platform.pause.subscribe(async () => {
      this.appIsActive = false;
    });

    this.platformResumeEventSubscription = this.platform.resume.subscribe(async () => {
      this.appIsActive = true;
      if (this.isCurrentUrlThisPage()) {
        this.refreshReleaseHistory();
      }
    });
  }

  ngOnInit(): void {
    this.pageTitleService.setTitle('PrintHistory');
  }

  public userSignInMethod = computed(() => {
    const user = this.user();
    if (
      user &&
      user.embedded?.authentication_details &&
      user.embedded.authentication_details.source
    ) {
      return this.signInMethods[user.embedded.authentication_details.source] || '';
    }
    return '';
  });

  private isCurrentUrlThisPage() {
    let isThisPage = false;
    let currentUrl = null;
    let urlTree = this.router.parseUrl(this.router.url);
    urlTree.queryParams = {}
    currentUrl = urlTree.toString();
    if (currentUrl && currentUrl === '/release-history') {
      isThisPage = true;
    };
    return isThisPage;
  }

  public refreshReleaseHistory(refresher?) {
    this.logger.info('refreshReleaseHistory()');
    this.refresher = refresher;
    this.dataIsLoaded = false;
    this.getReleaseHistory();
  }

  public getReleaseHistory() {
    this.logger.info('getReleaseHistory()');
      let currentUserUrl: string = this.tenant().links.currentUser;
      this.dialogService.showLoadingSpinnerDialog('getReleaseHistory()').subscribe(() => {
      this.releaseHistoryService.getPrintJobReleaseUrl(currentUserUrl)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((printJobReleaseUrl: string) => {
          this.releaseHistoryService.getReleaseHistory(printJobReleaseUrl, this.currentPage, this.pageSize, 'CREATED', 'DESC')
          .pipe(takeUntil(this.unSubscribe))
          .subscribe((printJobReleaseResource: IPrintJobReleaseResource) => {
            this.dialogService.hideLoadingSpinnerDialog('getReleaseHistory()');
            this.pagingObject = printJobReleaseResource['page'];
            this.printJobReleases = printJobReleaseResource['printJobReleases'];
            this.dataIsLoaded = true;
            if (this.refresher) {
              this.refresher.target.complete();
            }

            this.unSubscribe.next();
            this.unSubscribe.complete();
            this.handleReleaseJobStatus();
          }, (error) => {
              this.dataIsLoaded = true;
              if (this.refresher) {
                  this.refresher.target.complete();
                }
              this.dialogService.hideLoadingSpinnerDialog('ERROR getReleaseHistory()');
          });
      }, (error) => {
          this.dataIsLoaded = true;
          if (this.refresher) {
            this.refresher.target.complete();
          }
          this.dialogService.hideLoadingSpinnerDialog('ERROR getReleaseHistory() - getPrintJobReleaseUrl');
          this.errorService.handleHttpClientResponseError(error, 'GET', 'handleUserAuthentication')
          .subscribe(result => {
            if (result === 'unauthenticated') {
              this.logger.info('User unauthenticated');
            }
          });
      });
    });
  }

  public async presentInfoModal(event, target, modalType): Promise<void> {

    let dataPackage: any = {
      target: target.releaseJob,
      printer: target,
      releaseJobStatus: target.releaseJobStatus,
      cssClass: modalType
    };

    const infoModal = await this.modalCtrl.create({
      component: ModalComponent,
      componentProps: {dataPackage},
      cssClass: modalType
    });

    return await infoModal.present();
  }

  private handleReleaseJobStatus() {
    this.logger.info('handleReleaseJobStatus()');
      if (this.printJobReleases && this.printJobReleases.length > 0) {
        this.printJobReleases.forEach((PJRelease, releaseIndex) => {
            PJRelease.releaseJobs.forEach((releaseJob, jobIndex) => {
                if (releaseJob['releaseJobStatus'] === 'SUCCESS') {
                    this.logger.info('releaseJob["releaseJobStatus"]' + releaseJob['releaseJobStatus']);
                } else {
                    if (releaseJob['releaseJobStatus']['releaseProblem']) {
                      this.logger.info('handleReleaseJobStatus() ' + releaseJob["releaseJobStatus"]["releaseProblem"]);
                    } else {

                    }
                }
            });
        });
      }
    }

    public getNextPage(showPrevious?) {
        if (showPrevious) {
          this.currentPage--;
        } else {
          this.currentPage++;
        }
        this.refreshReleaseHistory();
    }

    private startListeners(): void {
    this.logger.info('startListeners()');
        if (!this.createdReleaseSubscription) {
            this.createdReleaseSubscription = this.broadcaster.on<string>(CHANNELS.SPECIFIC_USER.EVENTS.USER_RELEASE_CREATED)
            .subscribe((data) => {
                // check state to see if page needs refresh
                console.log('**PUSHER release CREATED', 'release history', data);
                this.logger.info('**PUSHER release CREATED');
                this.acceptedReleaseStates.forEach(state => {
                    if (data['state'] === state && this.appIsActive && this.isCurrentUrlThisPage()) {
                        this.refreshReleaseHistory();
                    }
                });
            });
        }
        if (!this.updatedReleaseSubscription) {
          this.updatedReleaseSubscription = this.broadcaster.on<string>(CHANNELS.SPECIFIC_USER.EVENTS.USER_RELEASE_UPDATED)
          .subscribe((data) => {
            // check newState param to see if release
            console.log('**PUSHER release UPDATED', 'release history', data);
            this.logger.info('**PUSHER release UPDATED');
            this.handleReleaseUpdate(data);
          });
        }

        if (!this.deletedReleaseSubscription) {
            this.deletedReleaseSubscription = this.broadcaster.on<string>(CHANNELS.SPECIFIC_USER.EVENTS.USER_RELEASE_DELETED)
            .subscribe((data) => {
                // remove the release from releases array
                console.log('**PUSHER release DELETED', 'release history', data);
                this.logger.info('**PUSHER release DELETED');
                let deletedReleaseIndex = this.findReleaseIndex(data['release']);

                if (deletedReleaseIndex !== -1 && this.appIsActive && this.isCurrentUrlThisPage()) {
                    this.refreshReleaseHistory();
                }
            });
        }

        this.languageChangeSubscription = this.broadcaster.on<string>('languageChange')
        .subscribe(data => {
          this.currentLanguage = this.localeService.formatLocaleString(data);
        });
    }

    private handleReleaseUpdate(data: string) {
        this.logger.info('handleReleaseUpdate()');

        // is there a change of state for the release //
        let stateChange: any = data['stateChange']['value'] ? data['stateChange']['value'] : null;
        let stateBefore: string = stateChange ? stateChange['stateBefore'] : null;
        let stateAfter: string = stateChange ? stateChange['stateAfter'] : null;

        // console.log('stateChange', stateChange);

        if (stateChange && stateBefore === 'CREATING' && this.appIsActive && this.isCurrentUrlThisPage()) { // is there a new release to see //
            this.refreshReleaseHistory();
        } else {
            let releaseIndex = this.findReleaseIndex(data['release']);
            // console.log('releaseIndex', releaseIndex);
            if (releaseIndex !== null) { // check if the upated release are in the currrent view //
                let releaseToUpdate = this.printJobReleases[releaseIndex];
                let updatedJobStates = data['releaseJobStates'];
                let currentReleaseVersion: number = data['version'];

                if (currentReleaseVersion > releaseToUpdate.version) {
                    releaseToUpdate.version = currentReleaseVersion;

                    releaseToUpdate.state = stateAfter;
                    // console.log('releaseToUpdate', releaseToUpdate);
                    releaseToUpdate.releaseJobs.forEach(job => {
                        // console.log('release to update jobs job', job);
                        // console.log('updatedJobStates', updatedJobStates);
                        updatedJobStates.forEach(jobState => {
                            let linkArray = jobState.job.split('/');
                            let updatedJobId = linkArray[linkArray.length -1];
                            // console.log('job.jobId', job.jobId);
                            // console.log('updatedJobId', updatedJobId);
                            if (job.jobId === updatedJobId) {
                                // console.log('jobState', jobState);
                                // console.log('job', job);
                                job.releaseJobStatus['inProgress'] =  jobState['inProgress'];
                                job.releaseJobStatus['isFailed'] =  jobState['isFailed'];
                                job.releaseJobStatus['isFinished'] =  jobState['isFinished'];
                                job.releaseJobStatus['jobState'] = jobState.jobState;
                            }
                        });
                    });
                } else {
                    console.log('## VERSION OUT OF ORDER');
                }
                // console.log('currentReleaseVersion', currentReleaseVersion);
            }
        }
    }

    private findReleaseIndex(releaseUrl: any): number {
        this.logger.info('findReleaseIndex()');
        let releaseIndex: number = null;
        if (this.printJobReleases && this.printJobReleases.length !== 0) {
            this.printJobReleases.forEach(release => {
                if (release.links.self === releaseUrl) {
                    releaseIndex = this.printJobReleases.indexOf(release);
                }
            });
        }
        return releaseIndex;
    }

    ionViewDidEnter(): void {
      this.pageTitleService.setTitle('PrintHistory');
      this.tenantService.refreshStoredTenant()
      .pipe(takeUntil(this.getTenantAndUserSubscription))
      .subscribe(() => {
        this.userService.refreshCurrentUser()
        .pipe(takeUntil(this.getTenantAndUserSubscription))
        .subscribe(() => {
          this.getTenantAndUserSubscription.next();
          this.getTenantAndUserSubscription.complete();
          if (this.user() && this.user().embedded.user.language) {
            this.currentLanguage = this.localeService.formatLocaleString(this.user().embedded.user.language);
          }
          this.startListeners();
          this.getReleaseHistory();
        });
      }, (error) => {
        this.errorService.handleHttpClientResponseError(error, 'GET', 'handleUserAuthentication')
        .subscribe(result => {
          if (result === 'unauthenticated') {
            this.logger.info('User unauthenticated');
          }
        });
      });
    }

  ionViewDidLeave(): void {
    this.createdReleaseSubscription.unsubscribe();
    this.deletedReleaseSubscription.unsubscribe();
    this.languageChangeSubscription.unsubscribe();
    this.platformResumeEventSubscription.unsubscribe();
    this.updatedReleaseSubscription.unsubscribe();
  }
}
